import { useApi } from './apiService';

export const useRemedActivity = () => {
  const api = useApi();

  const getRemedActivity = async (
    tenantID: any,
    pageNumber = 1,
    pageSize = 20,
    mode: 'OnDemand' | 'Automated',
    statuses?: string,
    groupId?: string,
    groupPolicyAutomationId?: string,
  ) => {
    try {
      const groupParam = groupId ? `&tenantGroupId=${groupId}` : '';
      let endpoint = `/patchcenterservice/RemediationPlan/${tenantID}/remediation-plan-activity?remediationPlanType=${mode}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      if (statuses) {
        endpoint += `&statuses=CompletedWithErrors${groupParam}`;
      }
      if (groupPolicyAutomationId) {
        endpoint += `&groupPolicyAutomationId=${groupPolicyAutomationId}`;
      }
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity:', error);
      throw error;
    }
  };

  return { getRemedActivity };
};

export const useDetailActivity = () => {
  const api = useApi();

  const getDetailActivity = async (agentID: string, remediationID: string) => {
    try {
      const response = await api.get(`/patchcenterservice/RemediationPlan/${agentID}/remediation-plan-agent-details/${remediationID}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity detail:', error);
      throw error;
    }
  };

  return { getDetailActivity };
};

export const useActivityLogs = () => {
  const api = useApi();

  const getActivityLogs = async (remediationPlanId: string, agentId: string) => {
    try {
      const response = await api.get(`patchcenterservice/RemediationPlan/${remediationPlanId}/remediation-plan-task/${agentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity:', error);
      throw error;
    }
  };

  return { getActivityLogs };
};

export const useTaskExecutionHistory = () => {
  const api = useApi();

  const getTaskExecutionHistory = async (agentId: string, taskId: string) => {
    try {
      const response = await api.get(`/agentapi/agent-task-execution-history?agentId=${agentId}&taskId=${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task execution history:', error);
      throw error;
    }
  };

  return { getTaskExecutionHistory };
};

export const useActivityRollback = () => {
  const api = useApi();

  const getRollbackActivity = async (tenantId: string, remediationPlanId: string, remediationPlanAgentIds: string[]) => {
    try {
      const response = await api.post(`patchcenterservice/RemediationPlan/${tenantId}/rollback-remediation-plan`, {
        remediationPlanID: remediationPlanId,
        remediationPlanAgentIDs: remediationPlanAgentIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching rollback:', error);
      throw error;
    }
  };

  return { getRollbackActivity };
};

// Lightweight plan header + agent/activity counts. Cached server-side (1hr) - cheap to call on every page load.
export const useRemediationPlanBanner = () => {
  const api = useApi();

  const getRemediationPlanBanner = async (tenantId: string, remediationPlanId: string) => {
    try {
      const response = await api.get(`patchcenterservice/RemediationPlan/${tenantId}/remediation-plan-agents/${remediationPlanId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching remediation plan banner:', error);
      throw error;
    }
  };

  return { getRemediationPlanBanner };
};

// Backend-paginated per-device rows for the Device View - only the requested page is enriched from Cosmos.
export const useRemediationPlanAgentDetails = () => {
  const api = useApi();

  // viewMode matches the backend's AgentStatusViewMode enum ('Compact' | 'Detailed') - Compact is the
  // server-side default too, filtering by status now happens in the query, not client-side.
  const getRemediationPlanAgentDetails = async (
    tenantId: string,
    remediationPlanId: string,
    pageNumber = 1,
    pageSize = 20,
    viewMode: 'Compact' | 'Detailed' = 'Compact',
  ) => {
    try {
      const response = await api.get(
        `patchcenterservice/RemediationPlan/${tenantId}/remediation-plan-agents/${remediationPlanId}/details?pageNumber=${pageNumber}&pageSize=${pageSize}&viewMode=${viewMode}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching remediation plan agent details:', error);
      throw error;
    }
  };

  return { getRemediationPlanAgentDetails };
};

// Backend-paginated Patch View list: one row per patch/action/OS-update sub-task, with device count and
// a Completed/InProgress/Errors/Unknown status breakdown. Replaces the old per-device fan-out.
export const useRemediationPlanPatchSummary = () => {
  const api = useApi();

  const getRemediationPlanPatchSummary = async (tenantId: string, remediationPlanId: string, pageNumber = 1, pageSize = 20) => {
    try {
      const response = await api.get(
        `patchcenterservice/RemediationPlan/${tenantId}/remediation-plan-agents/${remediationPlanId}/patches?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching remediation plan patch summary:', error);
      throw error;
    }
  };

  return { getRemediationPlanPatchSummary };
};

// Backend-paginated device drill-down for a single patch/sub-task (identified by taskId from the patch summary).
export const useRemediationPlanPatchDevices = () => {
  const api = useApi();

  const getRemediationPlanPatchDevices = async (
    tenantId: string,
    remediationPlanId: string,
    taskId: string,
    pageNumber = 1,
    pageSize = 20,
  ) => {
    try {
      const response = await api.get(
        `patchcenterservice/RemediationPlan/${tenantId}/remediation-plan-agents/${remediationPlanId}/patches/${taskId}/devices?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching remediation plan patch devices:', error);
      throw error;
    }
  };

  return { getRemediationPlanPatchDevices };
};

export const useActivityRetry = () => {
  const api = useApi();

  const retryActivity = async (remediationPlanId: string) => {
    try {
      const response = await api.post(`patchcenterservice/RemediationPlan/retry-remediation-plan`, {
        remediationPlanID: remediationPlanId,
      });
      return response.data;
    } catch (error) {
      console.error('Error retrying remediation plan:', error);
      throw error;
    }
  };

  return { retryActivity };
};
